import {
  AccountInfo,
  AuthError,
  InteractionRequiredAuthError,
  InteractionStatus,
} from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { CustomBox } from "@components/CustomBox";
import Persona from "@components/Persona";
import { KeyboardEventHandler, useEffect, useMemo, useState } from "react";
import { defaultRequest, getTenantAuthority } from "./auth";
import groupBy from "lodash/groupBy";
import { TenantDisplayName } from "@components/graph-picker/TenantDisplayName";
import { TenantLogo } from "@components/graph-picker/TenantLogo";
import { AiFillCheckCircle } from "react-icons/ai";
import { TenantLookup } from "@components/graph-picker/TenantLookup";
import { PromptValue } from "@azure/msal-browser";
import { useQuery } from "@tanstack/react-query";
import { BsThreeDotsVertical } from "react-icons/bs";
import { VscAdd, VscAzure, VscPersonAdd, VscWarning } from "react-icons/vsc";
import { FaStar } from "react-icons/fa";
import useAccountStore from "@components/graph-picker/account-store";
import { useShallow } from "zustand/react/shallow";

const useMsalAccounts = () => {
  const msal = useMsal();
  const groupedAccounts = useMemo(() => {
    const groupedAccounts = groupBy(msal.accounts, (a) => a.tenantId);
    return Object.entries(groupedAccounts).map(([_, a]) => a);
  }, [msal.accounts]);
  return groupedAccounts;
};

export interface AccountPickerProps {
  activeAccount?: AccountInfo | null;
  onChange: (account: AccountInfo) => unknown;
  onTenantLogin?: (account: AccountInfo | null) => unknown;
}

export const AccountPicker = ({
  activeAccount,
  onChange,
  onTenantLogin = (a) => {},
}: AccountPickerProps) => {
  const msal = useMsal();
  const groupedAccounts = useMsalAccounts();

  const tenantSearchModal = useDisclosure();
  const [tenantAuthority, setTenantAuthority] = useState("");

  const loginToTenant = (tenantId: string) => {
    return msal.instance.loginPopup({
      ...defaultRequest,
      authority: getTenantAuthority(tenantId),
      prompt: PromptValue.SELECT_ACCOUNT,
      // Since MSAL v5 can't get events from users closing the popup we
      // pass in the override flag to force MSAL to treat a closed popup as a completed interaction and allow future popups to open.
      // see: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/login-user.md#handling-popup-interaction_in_progress-errors
      overrideInteractionInProgress: true,
    });
  };

  return (
    <Flex direction="column" gap={4}>
      {groupedAccounts.map((g) => (
        <CustomBox variant="card" pt={2} p={1} key={g[0].tenantId}>
          <Flex align="center" gap={2} px={2} py={1} minH={10}>
            <TenantLogo tenantId={g[0].tenantId} w={8} />
            <Box flex="1">
              <TenantDisplayName
                tenantId={g[0].tenantId}
                fontSize="sm"
                fontWeight="semibold"
                isTruncated
              />
            </Box>
            <IconButton
              size="sm"
              variant="ghost"
              aria-label="Add account from tenant"
              title={"Add account from tenant"}
              icon={<VscAdd />}
              onClick={() => {
                loginToTenant(g[0].tenantId);
              }}
            />
          </Flex>
          <CustomBox pt={2} p={1}>
            {g.map((a) => (
              <AccountOption
                key={a.localAccountId}
                account={a}
                onClick={onChange}
                isActive={activeAccount?.localAccountId === a.localAccountId}
              />
            ))}
          </CustomBox>
        </CustomBox>
      ))}

      <ButtonGroup w="full" variant="ghost" size="sm">
        <Button
          flex="1"
          color="neutral.50"
          leftIcon={<VscPersonAdd />}
          onClick={() => {
            msal.instance.loginPopup({
              ...defaultRequest,
              prompt: PromptValue.SELECT_ACCOUNT,
              // Since MSAL v5 can't get events from users closing the popup we
              // pass in the override flag to force MSAL to treat a closed popup as a completed interaction and allow future popups to open.
              // see: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/login-user.md#handling-popup-interaction_in_progress-errors
              overrideInteractionInProgress: true,
            });
          }}
        >
          Add Account
        </Button>
        <Button
          flex="1"
          color="neutral.50"
          leftIcon={<VscAzure />}
          onClick={() => tenantSearchModal.onOpen()}
          title="Log in to an account from a specific tenant. This is useful for signing in to guest accounts."
        >
          Log In to Tenant
        </Button>
      </ButtonGroup>

      <Modal
        isOpen={tenantSearchModal.isOpen}
        onClose={tenantSearchModal.onClose}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Entra Tenant Login</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap={4} color="neutral.50">
              <Text fontSize="sm">
                Enter an Entra tenant ID or domain name to log in to that
                tenant.
              </Text>
              <Box fontSize="xs" color="neutral.100" px={2}>
                <Text>ex:</Text>
                <Text px={2}>c3637a33-14e9-4b54-a28e-f7cac55a8d1b</Text>
                <Text px={2}>xboxstudios.com</Text>
              </Box>
              <TenantLookup
                onChange={(a) => {
                  setTenantAuthority(a);
                }}
              />
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="green"
              title={"Log in to tenant"}
              isDisabled={!tenantAuthority}
              onClick={() => {
                loginToTenant(tenantAuthority).then((r) => {
                  onTenantLogin(r.account);
                  tenantSearchModal.onClose();
                });
              }}
            >
              Log in
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

interface AccountOptionProps {
  account: AccountInfo;
  onClick: (a: AccountInfo) => unknown;
  isActive: boolean;
}

const AccountOption = ({ account, onClick, isActive }: AccountOptionProps) => {
  const msal = useMsal();
  const { preferredAccount, setPreferredAccount } = useAccountStore(
    useShallow((s) => ({
      preferredAccount: s.preferredAccount,
      setPreferredAccount: s.setPreferredAccount,
    }))
  );

  // React-query doesn't persist errors between fetches. Store the
  // error here so that we can use the error
  const [err, setErr] = useState<AuthError | null>(null);

  // Check for token errors. In most cases this
  // will be due to the account's refresh token being expired
  // Note: even though we're using the isErr state flag to handle error logic,
  //       we need to use at least one of the useQuery variables in the component
  //       for the query to run.
  const { isError, error } = useQuery({
    queryKey: [
      "tenant",
      account.tenantId,
      account.localAccountId,
      "checkAccountExpiration",
    ],
    queryFn: async () => {
      // We don't need to keep checking for a valid token if we alreay know it's bad
      if (!!err && err instanceof InteractionRequiredAuthError) {
        return;
      }
      const response = await msal.instance.acquireTokenSilent({
        ...defaultRequest,
        account: account,
        authority: getTenantAuthority(account.tenantId),
      });
      return response;
    },
    refetchOnWindowFocus: true,
    retry: false,
  });

  useEffect(() => {
    const e = error as AuthError | null;
    if (!err && !!e) {
      setErr(e);
    } else if (!!err && !e) {
      setErr(null);
    }
  }, [error, err]);

  const handleClick = () => {
    if (!!err && err instanceof InteractionRequiredAuthError) {
      msal.instance.acquireTokenRedirect({
        ...defaultRequest,
        account: account,
        authority: getTenantAuthority(account.tenantId),
      });
    }

    onClick(account);
  };

  const selectedDeviceBg = useColorModeValue("blue.100", "blue.800");
  const errorBg = useColorModeValue("red.100", "rgba(255, 0, 0, 0.1)");

  const isMsalInProgress = msal.inProgress !== InteractionStatus.None;
  const isPreferredAccount =
    account.tenantId === preferredAccount?.tenantId &&
    account.localAccountId === preferredAccount?.localAccountId;

  return (
    <CustomBox
      key={account.localAccountId}
      variant="selectable"
      px={2}
      py={1}
      tabIndex={0}
      title={err?.errorMessage}
      onKeyDown={
        ((e) => {
          if (e.key === "Enter") {
            handleClick();
          }
        }) as KeyboardEventHandler<HTMLDivElement>
      }
      onClick={handleClick}
      backgroundColor={isActive ? selectedDeviceBg : !!err ? errorBg : "none"}
      w="full"
      display="flex"
      alignItems="center"
      gap={2}
    >
      <Flex flexDirection="column" minW={2}>
        {isPreferredAccount ? (
          <Icon
            as={FaStar}
            color="hermes.500"
            title="preferred account"
            w="3"
          />
        ) : isActive ? (
          <Icon as={AiFillCheckCircle} color="green.500" w="3" />
        ) : isError || !!err ? (
          <Icon as={VscWarning} color="red.500" w="3" />
        ) : (
          <></>
        )}
      </Flex>
      <Box>
        <Persona
          userId={account.localAccountId}
          tenantId={account.tenantId}
          text={account.name}
          email={account.username}
          size="sm"
        />
      </Box>
      <Box flex="1" overflow="hidden">
        <Text
          fontSize="xs"
          fontWeight="semibold"
          isTruncated
          title={account.name}
        >
          {account.name}
        </Text>
        <Text
          fontSize="xs"
          color="neutral.100"
          isTruncated
          title={account.username}
        >
          {account.username || account.localAccountId}
        </Text>
      </Box>
      <Menu isLazy>
        <MenuButton
          as={IconButton}
          size="xs"
          variant="ghost"
          icon={<BsThreeDotsVertical />}
          onClick={(e) => e.stopPropagation()}
        />
        <MenuList>
          {!!err && (
            <MenuItem
              isDisabled={isMsalInProgress}
              onClick={() => {
                if (!isMsalInProgress) {
                  msal.instance.loginRedirect({
                    ...defaultRequest,
                    account,
                    loginHint: account.username,
                  });
                }
              }}
            >
              Fix account
            </MenuItem>
          )}
          <MenuItem
            title="Preferred account is the account Hermes will attempt to use by default."
            onClick={(e) => {
              e.stopPropagation();
              setPreferredAccount(isPreferredAccount ? undefined : account);
            }}
          >
            {isPreferredAccount
              ? "Clear preferred account"
              : "Set as preferred account"}
          </MenuItem>
          <MenuItem
            onClick={() => {
              // Clear the local token cache only — no server-side logout redirect,
              // so the user's session on the server is preserved and other apps stay logged in.
              // see: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/logout.md#skipping-the-server-sign-out
              msal.instance.clearCache({ account });
            }}
          >
            Sign Out
          </MenuItem>
        </MenuList>
      </Menu>
    </CustomBox>
  );
};
